import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { ChatEntity, CommentType } from 'src/app/models/entities/chat.entity';
import { UserEntity } from 'src/app/models/entities/user.entity';
import { ChatService } from 'src/app/services/api/chat.service';
import { UserService } from 'src/app/services/api/user.service';
import { HtmlEditorComponent } from '../html-editor/html-editor.component';
import { Utils } from 'src/app/utils';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { NotificationService } from 'src/app/services/api/notifications.service';
import { NotificationEntity } from 'src/app/models/entities/notification.entity';
import { BoardService } from 'src/app/services/api/board.service';
import { CardService } from 'src/app/services/api/card.service';
import { SharedStateService } from 'src/app/services/shared.state.service';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  standalone: false
})
export class CommentsComponent implements OnInit, OnChanges {
  @Input() public boardKey!: string;
  @Input() public cardKey!: string;
  @Input() public cardName: string | undefined;
  @Input() public boardName: string | undefined;
  @Input() public uploadFolder: string = '';
  @Input() public codes: { code: string; board: string }[] = [];

  @ViewChild('editor') editor!: HtmlEditorComponent;
  @ViewChild('commentInput') commentInput!: ElementRef;

  public currentUser: UserEntity | null = null;
  public comments: ChatEntity[] = [];
  public isEditorExpanded: boolean = false;
  public newComment: string = '';
  public editingComment: string | null = null;
  public replyingTo: ChatEntity | null = null;
  public isLoading: boolean = true;
  public isUploading: boolean = false;
  public commentType: CommentType = 'comment';
  public cleanMessages: { [key: string]: string } = {};
  private notifications: { [key: string]: any } = {};
  public users: UserEntity[] = [];
  private client: string = '';
  private mentionedUsers: UserEntity[] = [];

  constructor(
    private chatService: ChatService,
    private userService: UserService,
    private notificationService: NotificationService,
    private boardService: BoardService,
    private cardService: CardService,
    private sharedStateService: SharedStateService
  ) {
    this.userService.currentUser$.subscribe((user) => {
      if (user) {
        this.currentUser = user;
        console.log('DEBUG: Comments - Current user:', user.key, user.name);
      }
    });
  }

  ngOnInit() {
    this.loadComments();
    this.loadUsers();
    
    // If boardName is not provided, try to fetch it from the boardKey
    if (!this.boardName && this.boardKey) {
      this.fetchBoardName();
    }
  }
  
  private async fetchBoardName() {
    try {
      const doc = await this.boardService.getItem(this.boardKey).get().toPromise();
      if (doc && doc.exists) {
        const boardData = doc.data();
        if (boardData && boardData.name) {
          this.boardName = boardData.name;
          console.log('DEBUG: Comments - Fetched board name:', this.boardName);
        }
      }
    } catch (error) {
      console.error('Error fetching board name:', error);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // When the card changes (including switching to a brand-new, keyless card),
    // discard any in-progress draft so it doesn't carry over to the next card.
    if (changes['cardKey'] && !changes['cardKey'].isFirstChange()) {
      this.resetDraft();
    }

    if ((changes['boardKey'] || changes['cardKey']) && this.boardKey && this.cardKey) {
      console.log('DEBUG: Comments - boardKey or cardKey changed:', this.boardKey, this.cardKey);
      this.loadComments();

      // If board key changed and we don't have a board name, try to fetch it
      if (changes['boardKey'] && !this.boardName) {
        this.fetchBoardName();
      }
    } else if (changes['cardKey'] && !this.cardKey) {
      // Navigated to a new card with no key yet — clear the previous card's comment list.
      this.comments = [];
      this.isLoading = false;
    }
  }

  /** Clears the comment editor / draft state so nothing leaks between cards. */
  private resetDraft() {
    this.newComment = '';
    this.isEditorExpanded = false;
    this.editingComment = null;
    this.replyingTo = null;
    this.commentType = 'comment';
    this.mentionedUsers = [];
  }

  private loadUsers() {
    this.userService.get().subscribe(users => {
      this.users = users.filter((x: any) => x);
      console.log('DEBUG: Comments - Loaded users count:', this.users.length);
    });
  }

  public onPauseSave(isPaused: boolean) {
    this.isUploading = isPaused;
  }

  private loadComments() {
    this.isLoading = true;
    console.log('DEBUG: Comments - Loading comments for boardKey:', this.boardKey, 'cardKey:', this.cardKey);
    this.chatService
      .getItems(this.boardKey, this.cardKey)
      .valueChanges({ idField: 'key' })
      .subscribe({
        next: (comments: ChatEntity[]) => {
          console.log('DEBUG: Comments - Raw comments from Firebase:', comments.length, 'comments received');
          console.log('DEBUG: Comments - First few comments:', comments.slice(0, 2));
          
          // Ensure all dates are properly converted to Date objects
          const transformedComments = comments.map(comment => {
            const converted = Utils.convertTimestampsInObject(comment) as ChatEntity;
            
            // Ensure createdAt and updatedAt are proper Date objects
            if (converted.createdAt && !(converted.createdAt instanceof Date)) {
              if (typeof converted.createdAt === 'string') {
                converted.createdAt = new Date(converted.createdAt);
              } else if (typeof converted.createdAt === 'object' && 'toDate' in converted.createdAt) {
                converted.createdAt = (converted.createdAt as any).toDate();
              }
            }
            
            if (converted.updatedAt && !(converted.updatedAt instanceof Date)) {
              if (typeof converted.updatedAt === 'string') {
                converted.updatedAt = new Date(converted.updatedAt);
              } else if (typeof converted.updatedAt === 'object' && 'toDate' in converted.updatedAt) {
                converted.updatedAt = (converted.updatedAt as any).toDate();
              }
            }
            
            return converted;
          });
          
          console.log('DEBUG: Comments - Transformed comments:', transformedComments.length);
          
          // Sort by createdAt in descending order (newest first)
          this.comments = transformedComments.sort((a, b) => {
            const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
            const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
            return dateB.getTime() - dateA.getTime();
          });
          
          console.log('DEBUG: Comments - Sorted comments to display:', this.comments.length);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading comments:', error);
          this.isLoading = false;
        }
      });
  }

  public expandEditor() {
    this.isEditorExpanded = true;
    setTimeout(() => {
      if (this.editor) {
        this.editor.setEditMode();
        const editorElement = document.querySelector('.editor-container');
        if (editorElement) {
          editorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 100);
  }

  private sendNotifications(recipients: string[], comment: ChatEntity) {
    // Send push notifications to users with push tokens
    recipients.forEach(recipientKey => {
      const user = this.users.find(u => u.key === recipientKey);
      if (user && this.currentUser && user.key !== this.currentUser.key) {
        this.notificationService.sendNotification(user, comment);
      }
    });
  }

  private saveNotifications() {
    for (const key of Object.keys(this.notifications)) {
      const notify: NotificationEntity = new NotificationEntity();
      notify.createdAt = new Date();
      notify.updatedAt = new Date();
      notify.client = this.client;
      notify.card = this.cardName ? this.cardName : '';
      notify.board = this.boardName || '';
      notify.cardKey = this.cardKey;
      notify.boardKey = this.boardKey;
      notify.type = 'chat';
      notify.key = key;
      notify.chats = this.notifications[key];
      notify.recipientName = this.users?.find((x) => x.key == key)?.name ?? '';
      notify.recipientKey = key;

      this.notificationService
        .create(key, notify, this.newComment)
        .then(() => {
          this.notifications = {};
        })
        .catch(error => {
          console.error('Error creating notification:', error);
        });
    }
  }

  public onUserMentioned(user: UserEntity) {
    if (user && !this.mentionedUsers.some(u => u.key === user.key)) {
      this.mentionedUsers.push(user);
    }
  }

  private extractMentionedUserKeys(message: string): string[] {
    // This method is now a backup for parsing mentions that might not have been caught by the editor
    if (!message || !this.users) return [];

    const mentionedUserKeys: string[] = [];
    const words = message.split(' ');

    for (const word of words) {
      if (word.startsWith('@')) {
        const username = word.substring(1); // Remove the @ symbol
        const mentionedUser = this.users.find((user) => user.name === username);
        if (mentionedUser && mentionedUser.key) {
          mentionedUserKeys.push(mentionedUser.key);
        }
      }
    }

    return mentionedUserKeys.filter((key) => key !== undefined);
  }

  public async saveComment() {
    if (!this.currentUser || !this.newComment.trim() || this.isUploading) return;

    // Editing an existing comment — update it in place, no new doc,
    // no count bumps, no notifications, no AI trigger.
    if (this.editingComment) {
      try {
        await this.chatService.update(this.boardKey, this.editingComment, {
          message: this.newComment,
          updatedAt: new Date(),
        } as Partial<ChatEntity> as ChatEntity);
        delete this.cleanMessages[this.editingComment];
        this.resetDraft();
      } catch (error) {
        console.error('Error updating comment:', error);
      }
      return;
    }

    // Get mentioned users from both the editor events and fallback text parsing
    const mentionedUserKeysFromText = this.extractMentionedUserKeys(this.newComment);
    const mentionedUserKeysFromEvents = this.mentionedUsers.map(user => user.key);
    
    // Combine both sources and remove duplicates
    const mentionedUserKeys = [...new Set([...mentionedUserKeysFromEvents, ...mentionedUserKeysFromText])];
    
    // Add the reply recipient if replying to someone
    const recipients = [...mentionedUserKeys];
    if (this.replyingTo && this.replyingTo.senderKey && !recipients.includes(this.replyingTo.senderKey)) {
      recipients.push(this.replyingTo.senderKey);
    }

    // IMPORTANT: Always add the current user (sender) to recipients
    // This ensures comments appear in both Comments and Chat components
    if (this.currentUser.key && !recipients.includes(this.currentUser.key)) {
      recipients.push(this.currentUser.key);
    }

    // Get card details to include participants in recipients
    let cardData: any = null;
    try {
      const cardDoc = await this.cardService.getItem(this.boardKey, this.cardKey).get().toPromise();
      if (cardDoc && cardDoc.exists) {
        cardData = cardDoc.data();
        if (cardData && cardData.participants && Array.isArray(cardData.participants)) {
          // Add all participant user keys to recipients
          cardData.participants.forEach((participant: any) => {
            if (participant.userKey && !recipients.includes(participant.userKey)) {
              recipients.push(participant.userKey);
            }
          });
        }
        
        // Also add assignedTo if it exists and isn't already in recipients
        if (cardData && cardData.assignedTo && !recipients.includes(cardData.assignedTo)) {
          recipients.push(cardData.assignedTo);
        }
      }
    } catch (error) {
      console.error('Error fetching card participants:', error);
    }

    const comment: any = {
      message: this.newComment,
      senderName: this.currentUser.name,
      senderKey: this.currentUser.key,
      createdAt: new Date(),
      updatedAt: new Date(),
      file: '',
      replyTo: this.replyingTo,
      recipients: recipients,
      replyToSenderKey: this.replyingTo ? this.replyingTo.senderKey : '',
      boardKey: this.boardKey,
      cardKey: this.cardKey,
      card: this.cardKey,
      cardName: this.cardName || '',
      boardName: this.boardName || '',
      key: '',
      read: false,
      cardsMentioned: [],
    };

    if (this.commentType !== 'comment') {
      comment.commentType = this.commentType;
    }

    try {
      // Prepare notifications for mentioned users and reply recipient
      this.notifications = {};
      recipients.forEach(recipientKey => {
        if (recipientKey !== this.currentUser?.key) {
          let increment = 1;
          if (this.notifications[recipientKey]) {
            increment = this.notifications[recipientKey] + 1;
          }
          this.notifications[recipientKey] = increment;
        }
      });

      // Save the comment
      await this.chatService.save(this.boardKey, this.cardKey, comment).toPromise();

      // Update comment count and bug/enhancement counts on the card
      await this.updateCardCommentCount(1);
      if (this.commentType === 'bug' || this.commentType === 'enhancement') {
        await this.updateCommentTypeCounts(1, this.commentType);
      }
      
      // Send notifications
      this.saveNotifications();
      
      // Send push notifications
      this.sendNotifications(recipients, comment);
      
      // Check if AI agent was mentioned and trigger AI agent if needed
      await this.checkAndTriggerAIAgentFromComment(mentionedUserKeys, cardData);
      
      // Reset the form
      this.newComment = '';
      this.isEditorExpanded = false;
      this.replyingTo = null;
      this.commentType = 'comment';
      this.mentionedUsers = []; // Clear mentioned users for next comment
    } catch (error) {
      console.error('Error saving comment:', error);
    }
  }

  public startEditing(comment: ChatEntity) {
    if (comment.senderKey === this.currentUser?.key) {
      this.editingComment = comment.key;
      this.replyingTo = null;
      this.newComment = comment.message;
      this.expandEditor();
    }
  }

  public cancelEditing() {
    this.editingComment = null;
    this.newComment = '';
    this.isEditorExpanded = false;
  }

  public reply(comment: ChatEntity) {
    if (this.editingComment) {
      this.editingComment = null;
      this.newComment = '';
    }
    this.replyingTo = comment;
    this.expandEditor();
  }

  public async deleteComment(comment: ChatEntity) {
    if (comment.senderKey === this.currentUser?.key) {
      try {
        await this.chatService.delete(this.boardKey, this.cardKey, comment.key);

        // Decrement comment count and bug/enhancement counts on the card
        await this.updateCardCommentCount(-1);
        if (comment.commentType === 'bug' || comment.commentType === 'enhancement') {
          await this.updateCommentTypeCounts(-1, comment.commentType);
        }
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  }

  private async updateCardCommentCount(delta: number): Promise<void> {
    try {
      const cardDoc = await this.cardService.getItem(this.boardKey, this.cardKey).get().toPromise();
      if (cardDoc && cardDoc.exists) {
        const cardData = cardDoc.data() as any;
        const newCount = Math.max(0, (cardData?.count || 0) + delta);
        await this.cardService.update(this.boardKey, { key: this.cardKey, count: newCount });
      }
    } catch (error) {
      console.error('Error updating card comment count:', error);
    }
  }

  private async updateCommentTypeCounts(delta: number, type: 'bug' | 'enhancement'): Promise<void> {
    try {
      const cardDoc = await this.cardService.getItem(this.boardKey, this.cardKey).get().toPromise();
      if (cardDoc && cardDoc.exists) {
        const cardData = cardDoc.data() as any;
        const update: any = { key: this.cardKey };
        if (type === 'bug') {
          update.commentBugs = Math.max(0, (cardData?.commentBugs || 0) + delta);
        } else {
          update.commentEnhancements = Math.max(0, (cardData?.commentEnhancements || 0) + delta);
        }
        await this.cardService.update(this.boardKey, update);
      }
    } catch (error) {
      console.error('Error updating comment type counts:', error);
    }
  }

  // Check if AI agent was mentioned and trigger AI agent
  private async checkAndTriggerAIAgentFromComment(mentionedUserKeys: string[], cardData: any): Promise<void> {
    try {
      console.log('🔍 Checking AI agent mention trigger in comments:', { 
        mentionedUserKeys, 
        cardKey: cardData?.key,
        boardKey: this.boardKey 
      });
      
      // Get board data to check AI agent configuration
      const boardDoc = await this.boardService.getItem(this.boardKey).get().toPromise();
      if (!boardDoc || !boardDoc.exists) {
        console.log('❌ Board not found for AI agent check');
        return;
      }

      const boardData = boardDoc.data();
      
      // Support both new automationConfig and legacy aiAgentConfig structures
      const aiConfig = boardData?.automationConfig?.aiAgentConfig || boardData?.aiAgentConfig;
      
      console.log('🤖 AI Config found in comments:', { 
        hasAutomationConfig: !!boardData?.automationConfig?.aiAgentConfig,
        hasLegacyConfig: !!boardData?.aiAgentConfig,
        enabled: aiConfig?.enabled,
        aiUserKey: aiConfig?.aiUserKey
      });
      
      if (!boardData || !aiConfig || !aiConfig.enabled) {
        console.log('❌ AI agent not configured or disabled for this board');
        return;
      }
      
      // Check if AI agent user was mentioned
      const aiUserMentioned = mentionedUserKeys.includes(aiConfig.aiUserKey);
      
      console.log('🎯 AI mention check:', {
        aiUserKey: aiConfig.aiUserKey,
        mentionedUserKeys,
        aiUserMentioned
      });
      
      if (aiUserMentioned && cardData) {
        console.log('🤖 AI Agent mentioned in comment, triggering AI agent');
        
        // Use the shared state service to trigger the AI agent
        // We need to call the same method that handles card assignments
        await this.sharedStateService.triggerAIAgentFromComment(cardData, aiConfig);
      }
    } catch (error) {
      console.error('Error checking AI agent mention:', error);
    }
  }

  public onPreviewUrls(comment: ChatEntity, urls: string[]): void {
    if (!urls.length || !comment.key) return;
    let cleaned = comment.message;
    for (const url of urls) {
      const escaped = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Clean up <a> tags where the link text is the URL itself
      cleaned = cleaned.replace(new RegExp(`<a([^>]*)>${escaped}</a>`, 'g'), '');
      // Remove standalone URL text (not inside href attributes)
      cleaned = cleaned.replace(new RegExp(`(?<!href=["'])${escaped}`, 'g'), '');
    }
    this.cleanMessages[comment.key] = cleaned;
  }

  // Track comments by key for better rendering performance
  public trackByKey(index: number, comment: ChatEntity): string {
    return comment.key;
  }
} 